import { useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { usePatientStore } from '../stores/patientStore'
import { useNotificationStore } from '../stores/notificationStore'
import { api } from '../services/api'

/**
 * CCOW context synchronization hook.
 *
 * Listens for `context.changed` WebSocket events from the server
 * (triggered when another application sets the patient context) and:
 *   - Updates `patientStore.currentPatient`
 *   - Navigates to the patient chart if the user is on a patient page
 *   - Shows a brief toast notification
 *
 * The events arrive via the notification store's dedicated context channel
 * (`publishContextEvent`), carrying the raw payload — NOT via the bell inbox,
 * whose ingest formats every event to human-readable prose that cannot be
 * parsed back. Each event is processed exactly once by sequence number, so an
 * interleaving notification can no longer displace one (the old code peeked
 * only at `notifications[0]`).
 *
 * When the USER changes patient (opens a chart), this hook also
 * POSTs to `/api/v1/context/set` to notify other connected apps.
 */
export function useContextSync() {
  const navigate = useNavigate()
  const location = useLocation()
  const currentPatient = usePatientStore((s) => s.currentPatient)
  const setCurrentPatient = usePatientStore((s) => s.setCurrentPatient)
  const contextEvent = useNotificationStore((s) => s.contextEvent)
  const prevPatientIdRef = useRef<number | null>(null)
  const lastProcessedSeqRef = useRef<number>(0)

  // Follow inbound context changes. The banner patient is fetched, not
  // fabricated: writing a stub (empty DOB, `isVip: false`, name ?? 'Unknown')
  // over the real patient would silently clear VIP/confidentiality signals.
  useEffect(() => {
    if (!contextEvent || contextEvent.seq <= lastProcessedSeqRef.current) return
    lastProcessedSeqRef.current = contextEvent.seq

    const payload = (contextEvent.payload ?? {}) as Record<string, unknown>
    // The wire payload is the backend's snake_case ContextState; accept the
    // camelCase spellings too so a future envelope rename cannot silently
    // break follow-me the way the prose-format regression did.
    const rawId = payload.patientId ?? payload.patient_id
    const patientId = typeof rawId === 'number' ? rawId : Number(rawId)
    if (!Number.isFinite(patientId) || patientId <= 0) return

    // Avoid re-processing a context we set ourselves.
    if (currentPatient?.patientId === patientId) return

    const patientName =
      (payload.patientName as string | undefined) ??
      (payload.patient_name as string | undefined)
    const patientMrn =
      (payload.patientMrn as string | undefined) ??
      (payload.patient_mrn as string | undefined)

    let cancelled = false
    const applyContext = async () => {
      try {
        const response = await api.patients.get(patientId)
        if (cancelled) return
        const patient = response.data
        setCurrentPatient({
          patientId: patient.patientId,
          mrn: patient.mrn,
          fullName: patient.fullName,
          dateOfBirth: patient.dateOfBirth,
          gender: patient.genderLegal,
          allergies: [],
          codeStatus: null,
          isVip: patient.isVip,
          isActive: patient.isActive,
        })
      } catch {
        // Best-effort follow: the chart id still routes even when the fetch
        // fails (offline, RLS-scoped-away). Prefer the payload's own
        // name/MRN over inventing them.
        if (cancelled) return
        setCurrentPatient({
          patientId,
          mrn: patientMrn ?? '',
          fullName: patientName ?? 'Unknown',
          dateOfBirth: '',
          gender: '',
          allergies: [],
          codeStatus: null,
          isVip: false,
          isActive: true,
        })
      }

      if (cancelled) return
      // Show toast notification
      toast.info(`Context changed to ${patientName ?? `Patient #${patientId}`}`)

      // If user is on a patient page, navigate to the new patient
      if (location.pathname.startsWith('/patients/')) {
        navigate(`/patients/${patientId}/demographics`)
      }
    }
    void applyContext()
    return () => {
      cancelled = true
    }
  }, [contextEvent, currentPatient, setCurrentPatient, navigate, location.pathname])

  // Notify the server when the user changes patient context
  const syncContextToServer = useCallback(
    async (patientId: number, encounterId?: number) => {
      try {
        await api.context.set({ patientId, encounterId })
      } catch {
        // Silently fail — CCOW sync is best-effort
      }
    },
    []
  )

  // Watch for patient changes and sync to server
  useEffect(() => {
    const patientId = currentPatient?.patientId ?? null

    if (patientId && patientId !== prevPatientIdRef.current) {
      prevPatientIdRef.current = patientId
      syncContextToServer(patientId)
    } else if (!patientId) {
      prevPatientIdRef.current = null
    }
  }, [currentPatient, syncContextToServer])

  return { syncContextToServer }
}
